import React, {
    useCallback,
    useDeferredValue,
    useEffect,
    useRef,
    useState,
} from "react";
import avatar from "../../../assets/avatar-de-perfil.png";
import {
    buscarPessoasCadastradas,
    buscarUrlFotoPessoa,
    editarPessoaPlacaVeiculo,
} from "../../../api/requisicoes/pessoas";
import { adicionarRegistroAcesso } from "../../../api/requisicoes/registroAcessos";
import {
    listarLocaisDestino,
    listarPerfil,
} from "../../../api/requisicoes/variaveis";
import Loading from "../../../components/Loading";
import { useAlert } from "../../../context/AlertContext";
import { useConfirm } from "../../../context/ConfirmContext";
import VisitantesAutorizadosEntradaMobile from "../VisitantesAutorizadosEntradaMobile";
import "./style.css";

const FORMULARIO_INICIAL = {
    idPessoa: "",
    nomeCompleto: "",
    documento: "",
    perfilId: "",
    responsavel: "",
    placaVeiculo: "",
    localDestinoId: "",
    observacoes: "",
    foto: null,
};

const VALORES_ANTERIORES_INICIAIS = {
    perfilId: "",
    responsavel: "",
    placaVeiculo: "",
    localDestinoId: "",
    observacoes: "",
};

const MINIMO_CARACTERES_PESQUISA = 3;
const LIMITE_RESULTADOS_PESQUISA = 30;

const normalizarTexto = (valor) =>
    typeof valor === "string"
        ? valor
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, "")
              .toLowerCase()
        : "";

const limparString = (valor) =>
    String(valor || "")
        .replace(/[.,#!$%^&*;:{}=_`~()-]/g, "")
        .split("/")
        .join("")
        .trim();

const verificarCpf = (valor) => {
    const cpfLimpo = limparString(valor);
    return cpfLimpo.length === 11 && /^\d+$/.test(cpfLimpo);
};

const formatarCpf = (valor) => {
    let cpfFormatado = String(valor || "").replace(/\D/g, "").slice(0, 11);

    if (cpfFormatado.length > 9) {
        cpfFormatado = cpfFormatado.replace(
            /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
            "$1.$2.$3-$4"
        );
    } else if (cpfFormatado.length > 6) {
        cpfFormatado = cpfFormatado.replace(
            /^(\d{3})(\d{3})(\d{0,3})/,
            "$1.$2.$3"
        );
    } else if (cpfFormatado.length > 3) {
        cpfFormatado = cpfFormatado.replace(
            /^(\d{3})(\d{0,3})/,
            "$1.$2"
        );
    }

    return cpfFormatado;
};

const paraMaiusculas = (valor) =>
    typeof valor === "string" ? valor.toUpperCase() : "";

const indexarPessoaParaPesquisa = (pessoa) => ({
    ...pessoa,
    nomeNormalizado: normalizarTexto(pessoa.fullName),
    documentoNormalizado: normalizarTexto(limparString(pessoa.cpf)),
});

const RegistroEntradaMobile = ({
    mostrarVisitantes,
    setActiveComponent,
}) => {
    const [formulario, setFormulario] = useState(FORMULARIO_INICIAL);
    const [valoresAnteriores, setValoresAnteriores] = useState(
        VALORES_ANTERIORES_INICIAIS
    );
    const [pessoas, setPessoas] = useState([]);
    const [perfis, setPerfis] = useState([]);
    const [locaisDestino, setLocaisDestino] = useState([]);
    const [modalAberto, setModalAberto] = useState(false);
    const [buscaModal, setBuscaModal] = useState("");
    const [carregandoPessoas, setCarregandoPessoas] = useState(false);
    const [carregandoOpcoes, setCarregandoOpcoes] = useState(false);
    const [processando, setProcessando] = useState(false);
    const [erroOpcoes, setErroOpcoes] = useState("");
    const formularioRef = useRef(null);
    const buscaModalAdiada = useDeferredValue(buscaModal);

    const { showAlert } = useAlert();
    const { showConfirm } = useConfirm();

    const carregarPessoas = useCallback(async () => {
        setCarregandoPessoas(true);

        try {
            const dados = await buscarPessoasCadastradas();
            setPessoas(
                Array.isArray(dados)
                    ? dados.map(indexarPessoaParaPesquisa)
                    : []
            );
        } finally {
            setCarregandoPessoas(false);
        }
    }, []);

    useEffect(() => {
        carregarPessoas();
    }, [carregarPessoas]);

    useEffect(() => {
        let componenteAtivo = true;

        const carregarOpcoes = async () => {
            setCarregandoOpcoes(true);

            try {
                const [dadosPerfis, dadosLocais] = await Promise.all([
                    listarPerfil(),
                    listarLocaisDestino(),
                ]);

                if (componenteAtivo) {
                    setPerfis(
                        [...dadosPerfis].sort((perfilA, perfilB) =>
                            perfilA.profileType.localeCompare(
                                perfilB.profileType
                            )
                        )
                    );
                    setLocaisDestino(
                        [...dadosLocais].sort((localA, localB) =>
                            localA.localDestination.localeCompare(
                                localB.localDestination
                            )
                        )
                    );
                }
            } catch (erro) {
                console.error("Erro ao carregar perfis ou destinos:", erro);

                if (componenteAtivo) {
                    setErroOpcoes(
                        "Não foi possível carregar perfis ou destinos."
                    );
                }
            } finally {
                if (componenteAtivo) {
                    setCarregandoOpcoes(false);
                }
            }
        };

        carregarOpcoes();

        return () => {
            componenteAtivo = false;
        };
    }, []);

    const alterarCampo = (campo, valor) => {
        setFormulario((formularioAtual) => ({
            ...formularioAtual,
            [campo]: valor,
        }));
    };

    const limparFormulario = () => {
        setFormulario(FORMULARIO_INICIAL);
        setValoresAnteriores(VALORES_ANTERIORES_INICIAIS);
    };

    const fecharPesquisaPessoas = () => {
        setBuscaModal("");
        setModalAberto(false);
    };

    const selecionarPessoa = async (pessoa) => {
        if (pessoa.blocked) {
            return;
        }

        setProcessando(true);

        try {
            const foto = await buscarUrlFotoPessoa(pessoa.id);
            const perfilId = pessoa.profile?.id
                ? String(pessoa.profile.id)
                : "";
            const destinoValido =
                pessoa.destination?.id &&
                Number(pessoa.destination.id) !== 0;
            const localDestinoId = destinoValido
                ? String(pessoa.destination.id)
                : "";
            const responsavel = paraMaiusculas(pessoa.responsible);
            const placaVeiculo = paraMaiusculas(pessoa.plateVehicle);
            const observacoes = paraMaiusculas(pessoa.comments);

            setFormulario({
                idPessoa: pessoa.id,
                nomeCompleto: paraMaiusculas(pessoa.fullName),
                documento: pessoa.cpf || "",
                perfilId,
                responsavel,
                placaVeiculo,
                localDestinoId,
                observacoes,
                foto,
            });
            setValoresAnteriores({
                perfilId,
                responsavel,
                placaVeiculo,
                localDestinoId,
                observacoes,
            });
            setBuscaModal("");
            setModalAberto(false);

            window.setTimeout(() => {
                formularioRef.current?.scrollTo({ top: 0, behavior: "auto" });
            }, 0);
        } catch (erro) {
            if (erro.response) {
                showAlert(`Erro! ${erro.response.data.error}.`, "erro");
            } else {
                showAlert(
                    "Erro desconhecido, contate o administrador do sistema!",
                    "erro"
                );
            }
        } finally {
            setProcessando(false);
        }
    };

    const registrarEntrada = (evento) => {
        evento.preventDefault();

        if (
            !formulario.idPessoa ||
            !formulario.nomeCompleto ||
            !formulario.documento ||
            !formulario.perfilId ||
            !formulario.responsavel ||
            !formulario.placaVeiculo ||
            !formulario.localDestinoId
        ) {
            showAlert(
                "Por favor, preencha todos os campos obrigatórios.",
                "erro"
            );
            return;
        }

        showConfirm(
            `Deseja registrar o acesso de ${formulario.nomeCompleto}?`,
            async () => {
                setProcessando(true);

                try {
                    const dadosAlterados =
                        valoresAnteriores.placaVeiculo !==
                            formulario.placaVeiculo ||
                        valoresAnteriores.responsavel !==
                            formulario.responsavel ||
                        valoresAnteriores.localDestinoId !==
                            formulario.localDestinoId ||
                        valoresAnteriores.observacoes !==
                            formulario.observacoes ||
                        valoresAnteriores.perfilId !== formulario.perfilId;

                    if (dadosAlterados) {
                        await editarPessoaPlacaVeiculo(
                            formulario.idPessoa,
                            formulario.placaVeiculo,
                            formulario.responsavel,
                            Number(formulario.localDestinoId),
                            formulario.observacoes,
                            Number(formulario.perfilId)
                        );
                    }

                    await adicionarRegistroAcesso({
                        localDestinoId: Number(formulario.localDestinoId),
                        observacoes: formulario.observacoes,
                        placaVeiculo: formulario.placaVeiculo,
                        responsavel: formulario.responsavel,
                        idPessoa: formulario.idPessoa,
                    });

                    showAlert("Registro realizado com sucesso!", "sucesso");
                    limparFormulario();
                    await carregarPessoas();
                } catch (erro) {
                    if (erro.response) {
                        showAlert(
                            `Erro! ${erro.response.data.error}.`,
                            "erro"
                        );
                    } else {
                        showAlert(
                            "Erro desconhecido, contate o administrador do sistema!",
                            "erro"
                        );
                    }
                } finally {
                    setProcessando(false);
                }
            },
            () => {}
        );
    };

    const pesquisaNormalizada = normalizarTexto(buscaModalAdiada.trim());
    const documentoPesquisaNormalizado = normalizarTexto(
        limparString(buscaModalAdiada)
    );
    const pesquisaValida =
        buscaModalAdiada.trim().length >= MINIMO_CARACTERES_PESQUISA;
    const todosResultadosPesquisa = pesquisaValida
        ? pessoas.filter(
              (pessoa) =>
                  pessoa.nomeNormalizado.includes(pesquisaNormalizada) ||
                  pessoa.documentoNormalizado.includes(
                      documentoPesquisaNormalizado
                  )
          )
        : [];
    const pessoasFiltradas = todosResultadosPesquisa.slice(
        0,
        LIMITE_RESULTADOS_PESQUISA
    );

    if (mostrarVisitantes) {
        return (
            <VisitantesAutorizadosEntradaMobile
                setActiveComponent={setActiveComponent}
            />
        );
    }

    const carregando =
        carregandoPessoas || carregandoOpcoes || processando;

    return (
        <div className="registroEntradaMobile-tela">
            {carregando && <Loading />}

            <header className="registroEntradaMobile-header">
                <h1>Registro de Entrada</h1>
                <button
                    type="button"
                    className="registroEntradaMobile-agendados"
                    onClick={() =>
                        setActiveComponent("visitantes-autorizados-entrada")
                    }
                    aria-label="Abrir visitantes agendados"
                    title="Visitantes Agendados"
                >
                    <i className="fas fa-calendar-check" aria-hidden="true"></i>
                </button>
            </header>

            <form
                ref={formularioRef}
                className="registroEntradaMobile-formulario"
                onSubmit={registrarEntrada}
            >
                <button
                    type="button"
                    className="registroEntradaMobile-pesquisarPessoa"
                    onClick={() => setModalAberto(true)}
                >
                    <i className="fas fa-search" aria-hidden="true"></i>
                    Pesquisar Pessoa
                </button>

                <div className="registroEntradaMobile-identificacao">
                    <div className="registroEntradaMobile-foto">
                        <img
                            src={formulario.foto || avatar}
                            alt="Foto da pessoa selecionada"
                        />
                    </div>
                    <span>
                        {formulario.idPessoa
                            ? "Pessoa selecionada"
                            : "Selecione uma pessoa"}
                    </span>
                </div>

                <div className="registroEntradaMobile-campo">
                    <label htmlFor="registroEntradaMobile-nome">
                        Nome Completo
                    </label>
                    <input
                        id="registroEntradaMobile-nome"
                        type="text"
                        value={formulario.nomeCompleto}
                        readOnly
                        required
                    />
                </div>

                <div className="registroEntradaMobile-campo">
                    <label htmlFor="registroEntradaMobile-documento">
                        Documento
                    </label>
                    <input
                        id="registroEntradaMobile-documento"
                        type="text"
                        value={
                            verificarCpf(formulario.documento)
                                ? formatarCpf(formulario.documento)
                                : formulario.documento
                        }
                        readOnly
                        required
                    />
                </div>

                <div className="registroEntradaMobile-campo">
                    <label htmlFor="registroEntradaMobile-perfil">Perfil</label>
                    <select
                        id="registroEntradaMobile-perfil"
                        value={formulario.perfilId}
                        onChange={(evento) =>
                            alterarCampo("perfilId", evento.target.value)
                        }
                        disabled={carregandoOpcoes}
                        required
                    >
                        <option value="">Selecione um perfil</option>
                        {perfis.map((perfil) => (
                            <option key={perfil.id} value={perfil.id}>
                                {perfil.profileType}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="registroEntradaMobile-campo">
                    <label htmlFor="registroEntradaMobile-responsavel">
                        Responsável
                    </label>
                    <input
                        id="registroEntradaMobile-responsavel"
                        type="text"
                        value={formulario.responsavel}
                        onChange={(evento) =>
                            alterarCampo("responsavel", evento.target.value)
                        }
                        onBlur={(evento) =>
                            alterarCampo(
                                "responsavel",
                                evento.target.value.toUpperCase()
                            )
                        }
                        required
                    />
                </div>

                <div className="registroEntradaMobile-campo">
                    <label htmlFor="registroEntradaMobile-placa">
                        Placa do Veículo
                    </label>
                    <input
                        id="registroEntradaMobile-placa"
                        type="text"
                        value={formulario.placaVeiculo}
                        onChange={(evento) =>
                            alterarCampo("placaVeiculo", evento.target.value)
                        }
                        onBlur={(evento) =>
                            alterarCampo(
                                "placaVeiculo",
                                evento.target.value.toUpperCase()
                            )
                        }
                        required
                    />
                </div>

                <div className="registroEntradaMobile-campo">
                    <label htmlFor="registroEntradaMobile-destino">
                        Local de Destino
                    </label>
                    <select
                        id="registroEntradaMobile-destino"
                        value={formulario.localDestinoId}
                        onChange={(evento) =>
                            alterarCampo(
                                "localDestinoId",
                                evento.target.value
                            )
                        }
                        disabled={carregandoOpcoes}
                        required
                    >
                        <option value="">Selecione um destino</option>
                        {locaisDestino.map((local) => (
                            <option key={local.id} value={local.id}>
                                {local.localDestination}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="registroEntradaMobile-campo">
                    <label htmlFor="registroEntradaMobile-observacoes">
                        Observações
                    </label>
                    <textarea
                        id="registroEntradaMobile-observacoes"
                        value={formulario.observacoes}
                        onChange={(evento) =>
                            alterarCampo("observacoes", evento.target.value)
                        }
                        onBlur={(evento) =>
                            alterarCampo(
                                "observacoes",
                                evento.target.value.toUpperCase()
                            )
                        }
                    />
                </div>

                {erroOpcoes && (
                    <p className="registroEntradaMobile-erro">
                        {erroOpcoes}
                    </p>
                )}

                <button
                    type="submit"
                    className="registroEntradaMobile-botaoRegistrar"
                    disabled={carregando}
                >
                    Registrar Entrada
                </button>
            </form>

            {modalAberto && (
                <div
                    className="registroEntradaMobile-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="registroEntradaMobile-modalTitulo"
                >
                    <div className="registroEntradaMobile-modalConteudo">
                        <header className="registroEntradaMobile-modalHeader">
                            <h2 id="registroEntradaMobile-modalTitulo">
                                Selecionar Pessoa
                            </h2>
                            <button
                                type="button"
                                onClick={fecharPesquisaPessoas}
                                aria-label="Fechar pesquisa de pessoas"
                            >
                                <i className="fas fa-times" aria-hidden="true"></i>
                            </button>
                        </header>

                        <input
                            type="search"
                            className="registroEntradaMobile-modalPesquisa"
                            placeholder="Buscar por nome ou documento..."
                            value={buscaModal}
                            onChange={(evento) => {
                                const valorLimpo = evento.target.value.replace(
                                    /[^a-zA-ZÀ-ÖØ-öø-ÿ0-9\s]/g,
                                    ""
                                );
                                setBuscaModal(valorLimpo);
                            }}
                            autoFocus
                        />

                        <div className="registroEntradaMobile-modalLista">
                            {!pesquisaValida ? (
                                <p>
                                    Digite pelo menos 3 caracteres para
                                    pesquisar.
                                </p>
                            ) : pessoasFiltradas.length === 0 ? (
                                <p>Nenhuma pessoa encontrada.</p>
                            ) : (
                                <>
                                    {pessoasFiltradas.map((pessoa) => (
                                        <button
                                            type="button"
                                            className={`registroEntradaMobile-pessoaCard ${
                                                pessoa.blocked
                                                    ? "registroEntradaMobile-pessoaBloqueada"
                                                    : ""
                                            }`}
                                            key={pessoa.id}
                                            onClick={() =>
                                                selecionarPessoa(pessoa)
                                            }
                                            disabled={
                                                pessoa.blocked || processando
                                            }
                                        >
                                            <strong>{pessoa.fullName}</strong>
                                            <span>
                                                {verificarCpf(pessoa.cpf)
                                                    ? formatarCpf(pessoa.cpf)
                                                    : pessoa.cpf}
                                            </span>
                                            {pessoa.blocked && (
                                                <em>Bloqueada</em>
                                            )}
                                        </button>
                                    ))}
                                    {todosResultadosPesquisa.length >
                                        LIMITE_RESULTADOS_PESQUISA && (
                                        <p>
                                            Mostrando 30 de{" "}
                                            {todosResultadosPesquisa.length}.
                                            Refine sua pesquisa.
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RegistroEntradaMobile;
